import { useEffect, useMemo, useState } from "react";
import type { Assignment, Person, Project } from "../services/api";
import {
  createAssignment,
  deleteAssignment,
  listAssignments,
  listPeople,
  listProjects,
  updateAssignment,
} from "../services/api";
import { AssignmentForm, ASSIGNMENT_STATUSES, type AssignmentFormValues, toFormValues } from "./AssignmentForm";
import { Modal } from "./Modal";
import type { AlertState } from "../utils/errors";
import { getErrorAlertMessage } from "../utils/errors";

export function AssignmentPanel() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [includeExpired, setIncludeExpired] = useState(false);

  useEffect(() => {
    void refreshAssignments();
    void refreshPeople();
    void refreshProjects();
  }, []);

  async function refreshAssignments() {
    try {
      const data = await listAssignments();
      setAssignments(data);
      setAlert((current) => (current?.type === "error" ? null : current));
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("load assignments", err) });
    }
  }

  async function refreshPeople() {
    try {
      const data = await listPeople();
      setPeople(data);
      setAlert((current) => (current?.type === "error" ? null : current));
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("load people", err) });
    }
  }

  async function refreshProjects() {
    try {
      const data = await listProjects();
      setProjects(data);
      setAlert((current) => (current?.type === "error" ? null : current));
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("load projects", err) });
    }
  }

  function openCreateModal() {
    setEditing(null);
    setIsModalOpen(true);
  }

  function openEditModal(assignment: Assignment) {
    setEditing(assignment);
    setIsModalOpen(true);
  }

  function closeModal() {
    setEditing(null);
    setIsModalOpen(false);
  }

  async function handleSave(values: AssignmentFormValues) {
    if (!values.project_id || !values.person_email || !values.status) {
      setAlert({ type: "error", message: "Project, person, and status are required." });
      return;
    }

    setIsSaving(true);
    setAlert(null);

    try {
      if (values.assignment_id) {
        const { assignment_id, ...payload } = values;
        await updateAssignment(assignment_id, payload);
        setAlert({ type: "info", message: "Assignment updated." });
      } else {
        const { assignment_id: unusedAssignmentId, ...payload } = values;
        void unusedAssignmentId;
        await createAssignment(payload);
        setAlert({ type: "info", message: "Assignment created." });
      }
      await refreshAssignments();
      closeModal();
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("save this assignment", err) });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(assignment: Assignment) {
    if (!window.confirm("Delete assignment?")) return;
    try {
      await deleteAssignment(assignment.assignment_id);
      await refreshAssignments();
      setAlert({ type: "info", message: "Assignment deleted." });
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("delete this assignment", err) });
    }
  }

  const statusOptions = useMemo(() => ASSIGNMENT_STATUSES, []);

  const expiredProjectIds = useMemo(() => {
    return new Set(
      projects
        .filter((project) => (project.status ?? "").toUpperCase() === "EXPIRED")
        .map((project) => project.project_id),
    );
  }, [projects]);

  const filteredAssignments = useMemo(() => {
    return assignments.filter((assignment) => {
      if (!includeExpired && expiredProjectIds.has(assignment.project_id)) {
        return false;
      }
      if (!statusFilter) {
        return true;
      }
      return assignment.status === statusFilter;
    });
  }, [assignments, statusFilter, expiredProjectIds, includeExpired]);

  return (
    <div className="layout single-column">
      <section className="panel">
        <div className="panel-header">
          <h2>Assignment Filters</h2>
          <button type="button" className="primary" onClick={openCreateModal}>
            Create Assignment
          </button>
        </div>
        <div className="form-grid assignment-filter-grid">
          <label>
            Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label className="checkbox-field" style={{ flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span>Include expired requisitions</span>
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(event) => setIncludeExpired(event.target.checked)}
            />
          </label>
          <div className="filter-action-cell">
            <button type="button" className="ghost" onClick={() => void refreshAssignments()}>
              Refresh
            </button>
          </div>
        </div>
        {alert && (
          <div className={`alert ${alert.type}`}>
            {alert.message}
            {alert.details && alert.details.length > 0 && (
              <ul>
                {alert.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
      <section className="panel">
        <h2>Assignments</h2>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Person</th>
                <th>Status</th>
                <th>Created</th>
                <th>Start</th>
                <th>End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty">
                    No assignments match the current filters.
                  </td>
                </tr>
              ) : (
                filteredAssignments.map((assignment) => (
                  <tr key={assignment.assignment_id}>
                    <td>{assignment.project_id}</td>
                    <td>{assignment.person_email}</td>
                    <td>{assignment.status}</td>
                    <td>{formatDateOnly(assignment.created_at)}</td>
                    <td>{assignment.start_date ?? "-"}</td>
                    <td>{assignment.end_date ?? "-"}</td>
                    <td className="actions">
                      <button type="button" onClick={() => openEditModal(assignment)}>
                        Edit
                      </button>
                      <button type="button" className="danger" onClick={() => void handleDelete(assignment)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      <Modal open={isModalOpen} onClose={closeModal} title={editing ? "Edit Assignment" : "Create Assignment"}>
        <AssignmentForm
          initial={editing ? toFormValues(editing) : undefined}
          projects={projects}
          people={people}
          onSubmit={(values) => void handleSave(values)}
          onCancel={closeModal}
          isSaving={isSaving}
        />
      </Modal>
    </div>
  );
}

function formatDateOnly(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.split("T")[0] ?? value;
  }
  return date.toISOString().split("T")[0];
}
